<template>
  <div
    :id="id"
    class="submission-request-edit"
  >
    <p class="hint">
      {{ $t('project.submission.title') }}
    </p>
    <div class="submission-request-info">
      <div class="sr-completion-type">
        <div
          v-if="!isWorkCompleted"
          class="row completion-type"
        >
          <input
            :id="`sr-completionType-final-partial-${id}`"
            v-hkldinput
            type="checkbox"
            class="final-partial-checkbox"
            :checked="finalPartial"
            @change="event => setFinalPartialCompletionType(event)"
          >
          <label
            :for="`sr-completionType-final-partial-${id}`"
            class="final-completion"
          >
            {{ $t('project.submission.workComplete') }}
          </label>
          <label class="hint work-complete-hint">{{ workCompleteHintMsg }}</label>
        </div>
      </div>
      <div
        v-show="isDraftRetentionSubmission"
        class="row completion-type"
      >
        <label class="retention-completion">{{ retentionCompletionTypeView }}</label>
      </div>
      <div class="sr-completion-date">
        <label
          class="required-flag"
        >
          {{ $t('project.submission.completedOn') }}
        </label>
        <DatePicker
          :id="`sr-completed-date-${id}`"
          v-model="completedDate"
          format="DD MMM YY"
          :disabled="disabledDates"
          :disabledPicker="!canContractorEdit"
          :class="errCompletedDateClass"
          :calendarButton="true"
          calendarButtonIcon="fa fa-calendar"
        />
      </div>

      <div class="sr-service-received clearfix">
        <TextField
          v-hkldinput
          :label="requestLabel"
          required
          :value="submission.completedItems"
          :readonlyAsInput="!canContractorEdit"
          :hintBeforeInput="$t('hint.anyDeduction')"
          hintClass="hint sr-service-received-hint"
          inputTip="tooltip-class"
          @updateValue="val => updateFieldValue(val, 'completedItems')"
        />
      </div>
      <div>
        <InvoiceInfo
          v-if="showInvoiceModule"
          :submission="submission"
          :srIndex="srIndex"
          :highlightInvalidAmount="highlightCompletedAmount"
          :highlightInvoiceNo="highlightInvoiceNo"
          :canEdit="canContractorEdit"
        />
        <PaidStatus
          :submission="submission"
          :isContractor="isContractor"
          :isEdit="isEdit"
        />
      </div>
      <div>
        <po-balance-calculation
          :submission="submission"
          :srIndex="srIndex"
          :highlightInvalid="highlightCompletedAmount"
          @clear:errorHighlight="() => highlightCompletedAmount = false"
        />
      </div>
      <div
        v-if="showInvoiceModule"
        class="project-sr--invoice-attachment"
        :class="{'error-highlight': highlightInvoiceDoc}"
      >
        <div>
          <label class="required-flag">{{ $t('project.submission.invoice') }}</label>
          <p class="hint">
            {{ $t('hint.regionConfig.limitImg') }}
          </p>
        </div>
        <HkldDropzone
          v-if="canContractorEdit"
          :maxNumberOfFiles="1"
          uploadDocTypes=".pdf,.png,.jpg,.jpeg"
          :sourceId="(submission.invoice || {}).id || ''"
        />
        <EditAttachments
          ref="invoiceAttachment"
          :sourceId="(submission.invoice || {}).id || ''"
          :canEdit="canContractorEdit"
          :uploadSuccessCb="removeOldDocNClearHighlight"
          :getFilesList="getInvoiceFileList"
        />
      </div>

      <div class="project-sr-attachment clearfix">
        <div>
          <label>{{ $t('project.submission.supportDocs') }}</label>
          <p class="hint">
            {{ $t('hint.attachmentTips') }}
          </p>
        </div>
        <HkldDropzone
          v-if="canContractorEdit"
          :sourceId="submission.id"
        />
        <EditAttachments
          :sourceId="submission.id"
          :canEdit="canContractorEdit"
          :canDelete="canContractorEdit"
          :canDownloadAll="!canContractorEdit"
        />
      </div>

      <div class="row person-in-charge">
        <div class="small-3 medium-3 large-3 columns">
          <TextField
            :label="$t('PIC')"
            :value="personInCharge"
            :readonlyAsInput="!canContractorEdit"
            required
            @updateValue="val => updateFieldValue(val, 'personInCharge', 'name')"
          />
        </div>
        <div class="small-4 medium-4 large-4 columns">
          <TextField
            :label="$t('position')"
            :value="position"
            :readonlyAsInput="!canContractorEdit"
            required
            @updateValue="val => updateFieldValue(val, 'personInCharge', 'position')"
          />
        </div>
        <div class="small-5 medium-5 large-5 columns">
          <TextField
            :label="$t('onBehalfOf')"
            :value="companyName"
            :readonly="true"
          />
        </div>
      </div>
    </div>

    <div
      v-show="rejectHistory && rejectHistory.length > 0"
      class="action-history"
    >
      <div>
        <label>{{ $t('history') }}</label>
      </div>
      <reject-history
        v-for="(history, index) in rejectHistory"
        :key="index"
        :item="history"
        :isContractor="isContractor"
        :showRejectHeader="!index"
        :class="{'odd-row': index%2 }"
      />
    </div>
    <div class="sr-actions">
      <HKLButton
        action="save"
        color="orange"
        @click="() => save()"
      />
      <HKLButton
        :class="{ 'btn--disabled': disableSubmit }"
        action="submit"
        color="orange"
        @click="() => submit()"
      />
    </div>
    <ActionConfirmModal
      ref="confirmBalanceModal"
      :modalId="confirmBalanceModalId"
      :confirmBtnName="$t('action.yesSubmit')"
      :confirmTitle="$t('project.submission.remaingBalanceWorkCompletedHint')"
    />

    <modal
      ref="projectSrSubmitModal"
      modalId="project-sr-submit-modal"
      :showCloseBtn="true"
    >
      <div slot="content">
        <p class="modal-pure-text">
          {{ $t('hint.uploadRequiredDocs') }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange close-reveal-modal"
          @click="() => closeSubmitModal()"
        >{{ $t('action.ok') }}</button>
      </div>
    </modal>

    <modal
      ref="projectSrRefreshModal"
      modalId="project-sr-refresh-modal"
    >
      <div slot="content">
        <p class="modal-pure-text">
          {{ refreshModalHint }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--darkblue close-reveal-modal"
          @click="() => closeRefreshSubmissionModal()"
        >{{ $t('action.ok') }}</button>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import lodash from 'lodash'
import { getCurrentInstance, ref, unref, computed } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import DatePicker from '@/controls/DatePicker'
import EditAttachments from '@/controls/EditAttachments'
import HkldDropzone from '@/controls/HkldDropzone'
import PoBalanceCalculation from '@/modules/projects/submission-request/PoBalanceCalculation'
import RejectHistory from '@/modules/projects/documents/RejectHistory'
import TextField from '@/controls/TextField'
import HKLButton from '@/controls/Button/HKLButton.vue'
import * as folderApi from '@/api/folders/request'
import * as projectsApi from '@/api/projects/request'
import * as projectsUtil from '@/api/projects/util'
import { completionType as COMPLETION_TYPE, submissionStatus } from '@/utils/locale'
import { dateFormat } from '@/utils/regex'
import { sendEmail } from '@/api/postman/request'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { EmailTypes, ActionTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import notification from '@/utils/notification'
import mutationTypes from '@/store/mutation-types'
import Modal from '@/controls/Modal'
import { useStoreGetters } from '@/store/index'
import InvoiceInfo from '../InvoiceInfo.vue'
import { useHasPaid, useIsUnaccepted } from '../hook'
import PaidStatus from '../PaidStatus.vue'

export default {
  name: 'SubmissionRequestEdit',
  components: {
    ActionConfirmModal,
    DatePicker,
    EditAttachments,
    HkldDropzone,
    Modal,
    PoBalanceCalculation,
    RejectHistory,
    TextField,
    InvoiceInfo,
    HKLButton,
    PaidStatus,
  },

  props: {
    submission: Object,
    srIndex: {
      type: Number,
      default: 0,
    },
    isContractor: {
      type: Boolean,
      default: true,
    },
    companyName: String,
    isFirstSubmission: {
      type: Boolean,
      default: true,
    },
    disableSubmit: {
      type: Boolean,
      default: false,
    },
    draftSubmissionCounts: Number,
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const { backEndRegionalEnv } = useStoreGetters('regions', ['backEndRegionalEnv'])
    let returnData = {}

    let invoiceFiles = ref([])
    let highlightInvoiceDoc = ref(false)
    let highlightInvoiceNo = ref(false)
    const getInvoiceFileList = (list) => {
      invoiceFiles.value = list
    }
    const showInvoiceModule = computed(() => {
      return lodash.get(unref(backEndRegionalEnv), 'moduleSwitch.submissionInvoiceModule')
    })
    const getIsInvoiceNumDuplicate = (allSubmissions, toUpdateSubmission) => {
      const otherInvoiceNumber = allSubmissions
        .filter(submission => submission.id !== toUpdateSubmission.id)
        .map(sub => (lodash.get(sub, 'invoice.invoiceNumber') || '').trim().toUpperCase())
      const currentInvoiceNumber = lodash.get(toUpdateSubmission, 'invoice.invoiceNumber')
      return currentInvoiceNumber && otherInvoiceNumber.includes(currentInvoiceNumber.trim().toUpperCase())
    }
    const removeOldDocNClearHighlight = async () => {
      const invoiceDoc = proxy.$refs.invoiceAttachment
      const files = invoiceDoc.files
      const pendingClearFiles = lodash.dropRight(lodash.orderBy(files, ['createdAt'], ['asc']), 1)
      if (pendingClearFiles.length > 0) {
        await Promise.all(pendingClearFiles.map(file => {
          return invoiceDoc.deleteFile(file)
        }))
      }
      if (highlightInvoiceDoc.value) {
        highlightInvoiceDoc.value = false
      }
    }
    const highlightCompletedDate = ref(false)
    returnData = {
      ...returnData,
      removeOldDocNClearHighlight,
      getInvoiceFileList,
      invoiceFiles,
      highlightInvoiceDoc,
      showInvoiceModule,
      getIsInvoiceNumDuplicate,
      highlightInvoiceNo,
      errCompletedDateClass: computed(() => {
        return { 'error-highlight': unref(highlightCompletedDate) }
      }),
    }

    const hasPaid = useHasPaid(props.submission)
    const isUnAccept = useIsUnaccepted(props.submission, props.isContractor)
    const canContractorEdit = computed(() => {
      if (props.isContractor && unref(hasPaid)) return false
      return props.isContractor
    })
    returnData = {
      ...returnData,
      hasPaid,
      highlightCompletedDate,
      isUnAccept,
      canContractorEdit,
    }

    return returnData
  },

  data () {
    const datetime = (this.submission || {}).completedDate
    const completedDate = datetime ? new Date(datetime) : ''
    const personInChargeInfo = (this.submission || {}).personInCharge
    const personInCharge = (personInChargeInfo || {}).name
    const position = (personInChargeInfo || {}).position
    let completionType = (this.submission || {}).completionType || COMPLETION_TYPE.FULL
    const fromDate = new Date(Date.now())
    const disabledDates = {
      from: fromDate,
    }
    const rejectHistory = ((this.submission || {}).actionHistory || [])
      .filter(history => history.actionType)
    return {
      id: 'submission-request-form-' + this.submission.id,
      COMPLETION_TYPE,
      completionType,
      refreshModalHint: '',
      refresh: false,
      mutationBasePath: ['submissions', this.srIndex],
      completedDate,
      personInCharge,
      position,
      highlightCompletedAmount: false,
      disabledDates,
      rejectHistory,
      confirmBalanceModalId: `confirmBalance-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
    }
  },
  computed: {
    ...mapGetters('projects', [
      'currentProject', 'hasRetention', 'purchase',
    ]),
    requestLabel () {
      return this.isContractor
        ? this.$t('project.submission.vendorCompletedItems')
        : this.$t('project.submission.hklCompletedItems')
    },
    projectId () {
      return (this.currentProject || {}).id
    },
    isPartialCompletion () {
      return this.completionType.indexOf(COMPLETION_TYPE.PARTIAL) > -1
    },
    allSubmissions () {
      return (this.currentProject || {}).submissions || []
    },
    retentionNumber () {
      const apprFinalPartial = this.allSubmissions.find(submission => submission.status === submissionStatus.APPR &&
        submission.completionType === COMPLETION_TYPE.FINAL_PARTIAL)
      let retentionOrder = 0
      if (this.completionType === COMPLETION_TYPE.RETENTION_PAYMENT && apprFinalPartial) {
        const retentionNo = this.submission.order - apprFinalPartial.order
        retentionOrder = retentionNo > 1 ? retentionNo : 1
      }
      return retentionOrder
    },
    hasApprRetFinalPartial () {
      return this.hasRetention && this.isWorkCompleted
    },
    isWorkCompleted () {
      return this.allSubmissions.some(submission =>
        submission.status === submissionStatus.APPR &&
        submission.completionType === COMPLETION_TYPE.FINAL_PARTIAL)
    },
    workCompleteHintMsg () {
      return this.hasRetention
        ? this.$t('project.submission.withRetentionHint')
        : this.$t('project.submission.withoutRetentionHint')
    },

    finalPartial () {
      return this.submission.completionType === COMPLETION_TYPE.FINAL_PARTIAL
    },

    completeDate () {
      const date = (this.submission || {}).completedDate
      if (!date) {
        return ''
      }
      return dateUtil.convertDateToArray(date, dateFormat, '-')[0]
    },

    isDraftRetentionSubmission () {
      return [COMPLETION_TYPE.FINAL_PAYMENT, COMPLETION_TYPE.RETENTION, COMPLETION_TYPE.RETENTION_PAYMENT].includes(this.completionType) &&
      [submissionStatus.DRAFT, submissionStatus.REJECTED].includes(this.submission.status) && this.isWorkCompleted
    },
    retentionCompletionTypeView () {
      return this.$i18n.t('project.submission.retentionPayment', [this.retentionNumber])
    },
  },
  watch: {
    completedDate () {
      formUtils.setDirty()
      this.setCompletedDate()
    },
  },
  mounted () {
    if (!this.srSubmitModal) {
      this.srSubmitModal = this.$refs.projectSrSubmitModal
    }
    if (!this.srRefreshSubmissionModal) {
      this.srRefreshSubmissionModal = this.$refs.projectSrRefreshModal
    }
    // Submission is by order. Submissions after accepted final partial should be retention payment when edit.
    if (this.isWorkCompleted && this.hasRetention) {
      if (this.completionType !== this.COMPLETION_TYPE.RETENTION_PAYMENT) {
        this.setCompletionType(COMPLETION_TYPE.RETENTION_PAYMENT)
        this.completionType = COMPLETION_TYPE.RETENTION_PAYMENT
      }
      this.setRetentionOrder()
    }
    const retentionPaymentType = [this.COMPLETION_TYPE.RETENTION_PAYMENT, this.COMPLETION_TYPE.FINAL_PAYMENT, this.COMPLETION_TYPE.RETENTION]
    const isRjectFinalPartial = this.COMPLETION_TYPE.FINAL_PARTIAL === this.completionType && (this.submission || {}).status === submissionStatus.REJECTED
    const shouldSetToPartial = retentionPaymentType.includes(this.completionType) || isRjectFinalPartial
    if (!this.isWorkCompleted && shouldSetToPartial) {
      this.setCompletionType(COMPLETION_TYPE.PARTIAL)
      this.completionType = COMPLETION_TYPE.PARTIAL
    }
  },

  destroyed () {
    if (this.srSubmitModal && this.srSubmitModal.destroy) {
      this.srSubmitModal.destroy()
    }
    if (this.srRefreshSubmissionModal && this.srRefreshSubmissionModal.destroy) {
      this.srRefreshSubmissionModal.destroy()
    }
  },
  methods: {
    ...mapMutations('projects', {
      updateProjectField: mutationTypes.UPDATE_PATH_FIELD,
    }),
    ...mapActions('projects', [
      'saveSubmission',
      'updateSubmissionStatus',
      'loadProject',
    ]),
    updateFieldValue (newValue, ...fieldName) {
      const path = [...this.mutationBasePath, ...fieldName]
      this.updateProjectField({ path, newValue })
    },
    setCompletionType (newValue) {
      this.updateFieldValue(newValue, 'completionType')
    },
    setFinalPartialCompletionType (event) {
      const checked = event.target.checked
      const newValue = checked ? COMPLETION_TYPE.FINAL_PARTIAL : COMPLETION_TYPE.PARTIAL
      this.setCompletionType(newValue)
    },

    setCompletedDate () {
      const newValue = this.completedDate ? new Date(this.completedDate).getTime() : null
      this.updateFieldValue(newValue, 'completedDate')
      this.highlightCompletedDate = false
    },

    async invalidFolders () {
      const folders = await folderApi.getFolders(this.projectId)
      if (folders.length !== this.currentProject.folders.length) {
        await this.loadProject(this.projectId)
        EventBus.$emit('modules.projects.documents.folderUpdated')
      }
      return folders.some(folder => (folder.size === 0))
    },

    async deductionUpdated () {
      const project = await projectsApi.getProject(this.projectId)
      const amount = projectsUtil.getPurchaseAttrFromObject(project, 'amount') || '0'
      const deductionAmount = deep(this.submission, 'deductionAmount') + '' || '0'
      const projectDeduction = deep(project, 'deduction.amount') || '0'
      const retentionAmountPct = deep(project, 'retention.percentValue') || '0'
      const retentionAmount = retentionAmountPct * (amount - projectDeduction) / 100
      if (deductionAmount !== projectDeduction) {
        this.openRefreshSubmissionModal(this.$t('project.submission.deductionUpdatedHint'))
        this.updateFieldValue(projectDeduction, 'deductionAmount')
        this.updateFieldValue(retentionAmount, 'retentionAmount')
        const path = ['deduction', 'amount']
        this.updateProjectField({ path, newValue: projectDeduction })
        return true
      }
      return false
    },

    invalidSR () {
      fusionInputCheckUtil.validateAllFields(undefined, `#${this.id}`)
      let amountValue = deep(this.submission, 'completedAmount')
      this.highlightInvoiceDoc = this.showInvoiceModule && this.invoiceFiles.length !== 1
      this.highlightCompletedAmount = amountValue === '' || amountValue === 'undefined' || amountValue === null || amountValue < 0
      this.highlightCompletedDate = !deep(this.submission, 'completedDate') || deep(this.submission, 'completedDate') > this.disabledDates.from
      return $(`#${this.id} input.is-invalid-input`).length !== 0 ||
        $(`#${this.id} textarea.is-invalid-input`).length !== 0 ||
        this.highlightCompletedAmount || this.highlightCompletedDate || this.highlightInvoiceDoc ||
        !fusionInputCheckUtil.isAllFieldsValid(`#${this.id}`)
    },
    clearErrorClass () {
      this.highlightCompletedAmount = false
      this.highlightCompletedDate = false
      this.highlightInvoiceDoc = false
      this.highlightInvoiceNo = false
      fusionInputCheckUtil.resetAllFields()

      if ($('.error-highlight')) {
        $('.error-highlight').removeClass('error-highlight')
      }
    },

    async invalidDocTypes () {
      if (!(this.finalPartial ||
        this.submission.completionType === COMPLETION_TYPE.FULL)) {
        return false
      }

      const invalid = await this.invalidFolders()
      return invalid
    },

    getSubmissionForUpdate () {
      const submission = JSON.parse(JSON.stringify(this.submission))
      delete submission.actionHistory
      return Object.keys(submission)
        .reduce((partialData, fieldName) => {
          let field = {}
          field = { [fieldName]: submission[fieldName] }
          return { ...partialData, ...field }
        }, {})
    },
    checkAutoTick (remainingBalance, retentionAmount) {
      const noRentationAutoTick = !this.hasRetention && remainingBalance === 0
      const rentationAutoTick = this.hasRetention && remainingBalance <= retentionAmount
      if (noRentationAutoTick || rentationAutoTick) {
        this.setCompletionType(COMPLETION_TYPE.FINAL_PARTIAL)
      }
    },
    async submit () {
      const isDeductionUpdated = await this.deductionUpdated()
      if (isDeductionUpdated) return
      const allSubmissions = await projectsApi.getSubmissions({projectId: this.projectId}) || []
      const draftSubmissions = allSubmissions.filter(submission => submission.status === submissionStatus.DRAFT || submission.status === submissionStatus.REJECTED)
      if (this.draftSubmissionCounts !== draftSubmissions.length) {
        this.openRefreshSubmissionModal(this.$t('hint.refreshProject'))
        this.refresh = true
        return
      }
      if (this.invalidSR()) {
        return
      }
      const completedAmount = this.submission.completedAmount
      const amount = this.purchase.amount
      const previousTotalAmount = this.currentProject.completedAmount
      const deductionAmount = this.submission.deductionAmount
      const retentionAmount = this.submission.retentionAmount
      const remainingBalance = Number((amount - deductionAmount - previousTotalAmount - completedAmount).toFixed(2))
      if (this.submission.completionType === COMPLETION_TYPE.PARTIAL) {
        this.checkAutoTick(remainingBalance, retentionAmount)
      }

      const invalidDocTypes = await this.invalidDocTypes()
      if (invalidDocTypes) {
        this.openSubmitModal()
        return
      }

      this.setRetentionOrder()

      const submission = this.getSubmissionForUpdate()
      const hasApprRetFinalPartial = this.hasRetention && this.isWorkCompleted
      const isFinalPayment = hasApprRetFinalPartial && submission.completionType === COMPLETION_TYPE.RETENTION_PAYMENT && remainingBalance === 0
      if (isFinalPayment) {
        submission.completionType = COMPLETION_TYPE.FINAL_PAYMENT
      }
      if (!hasApprRetFinalPartial) {
        submission.retentionOrder = 0
      }
      submission.submittedDate = Date.now()
      const status = submissionStatus.OPEN

      if (remainingBalance < 0) {
        notification.fireNotification(false, this.$i18n.t('project.submission.remaingBalanceNegativeHint', [this.showInvoiceModule ? this.$i18n.t('project.submission.invoiceAmountWithCompletion') : this.$i18n.t('project.submission.completionAmount')]))
        this.highlightCompletedAmount = true
        return false
      }
      if (this.getIsInvoiceNumDuplicate(allSubmissions, submission)) {
        notification.fireNotification(false, this.$i18n.t('project.submission.invoiceNoDuplicate'))
        this.highlightInvoiceNo = true
        return
      }
      await this.saveSubmission({ submission, showNotification: false })
      await this.doSubmit(submission, status)
      formUtils.resetDirty()
    },

    async doSubmit (submission, status) {
      await this.updateSubmissionStatus({ status, id: submission.id, action: ActionTypes.submissions.submit })
      await this.loadProject(this.projectId)
      this.$emit('refreshPage')
      await sendEmail(EmailTypes.projects.submissionSubmitted, { order: submission.order, index: this.srIndex })
    },
    checkAllGivenInputsValid () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'})
      return fusionInputCheckUtil.isAllFieldsValid('.submission-request-edit')
    },
    async save () {
      this.clearErrorClass()
      const latestSr = (await projectsApi.getSubmissions({id: this.submission.id}) || [])[0]
      if (!latestSr || [submissionStatus.OPEN, submissionStatus.APPR].includes(latestSr.status)) {
        this.openRefreshSubmissionModal(this.$t('hint.refreshProject'))
        this.refresh = true
        return
      }
      if (!this.checkAllGivenInputsValid()) return
      const isDeductionUpdated = await this.deductionUpdated()
      if (isDeductionUpdated) return
      this.setRetentionOrder()
      const submission = this.getSubmissionForUpdate()
      submission.status = submissionStatus.DRAFT
      await this.saveSubmission({
        projectId: this.projectId,
        submission,
      })
      await this.loadProject(this.projectId)
      this.$emit('refreshPage')
      formUtils.resetDirty()
    },
    setRetentionOrder () {
      this.updateFieldValue(this.retentionNumber, 'retentionOrder')
    },
    openSubmitModal () {
      this.srSubmitModal.open()
    },
    closeSubmitModal () {
      this.srSubmitModal.close()
    },
    openRefreshSubmissionModal (hintText) {
      this.refreshModalHint = hintText
      this.srRefreshSubmissionModal.open()
    },
    closeRefreshSubmissionModal () {
      if (this.refresh) {
        this.loadProject(this.projectId)
      }
      this.refresh = false
      this.srRefreshSubmissionModal.close()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/project/style';
@import '~styles/popup';

.hint {
  font-size: 12px;
}
::v-deep .error-highlight {

    .vue-dropzone {
      background-color: #f9ecea
    }
  }
.work-complete-hint {
  font-size: 12px;
  font-style: italic;
  align-self: center;
}

.disabled-sr-completion-type {
  color: #6e7a91;
}

.submission-request-edit {
  padding: auto 15px;
}

.sr-service-received {
  margin-top: 5px;
}

.projects-po-balance-cal-view {
  margin-top: 10px;
}

.person-in-charge {
  padding-bottom: 10px;
}

.action-history {
  padding-top: 10px;
  border-top: 1px solid #e1e1e1;
}

.completion-type {
  input[type='radio'], input[type='checkbox'] {
    margin-top: 5px;
    display: inline-block;
  }
}
.completion-type {
  label {
    margin: 3px 0 0 15px;
    font-weight: normal;
    text-transform: none;
  }
  .retention-completion {
    font-weight: normal;
    margin-left: 0;
  }
}
.sr-completion-type input[type='checkbox'] {
  margin-top: 5px;
}

.final-completion {
  font-weight: normal;
}

.project-sr-attachment {
  margin-top: 10px;
  margin-right: 15px;
}

.sr-actions {
  margin: 15px auto;
  text-align: right;
  a {
    &:hover {
      color: #fefefe;
    }
  }
  .btn--disabled {
    cursor: default;
    pointer-events: none;
    background-color: #d3d3d3;
    color: #fefefe;
    border-bottom: 2px solid #c5c5c5;
  }
}

</style>

<style lang="scss">
.submission-request-edit {
  .hint.sr-service-received-hint{
    margin-bottom: 0;
  }

  .edit-attachments {
    .number {
      text-align: left;
    }
    th.number {
      padding-left: 0.625rem;
    }
  }
}
</style>
